import { Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { Advertiser, CoinReward, ProductReward, RewardData, StampReward } from 'src/app/api/api-campaigns.service.generated';
import { AdvertiserCampaignRewardEditComponent } from '../../advertiser-campaign-reward-edit/advertiser-campaign-reward-edit.component';
import { PunchCardCampaignService } from '../../punch-card-campaign-edit/punch-card-campaign-edit.service';
import { PhotoComponent, ButtonComponent } from 'wecheer-common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-reward-select',
  templateUrl: './reward-select.component.html',
  styleUrls: ['./reward-select.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    PhotoComponent,
    ButtonComponent
  ]
})
export class RewardSelectComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  punchCardCampaignService = inject(PunchCardCampaignService);


  @Input() advertiserId: string;
  @Input() title = 'Reward';

  advertiser: Advertiser;

  form = this.fb.group({
    productReward: new FormControl(null),
    coinReward: new FormGroup({ coins: new FormControl(null, Validators.min(0)) }),
    stampReward: new FormGroup({ stamps: new FormControl(null, Validators.min(0)) }),
    rewardTitle: new FormControl(null),
    rewardDescription: new FormControl(null),
  });

  rewardProductsOptions: ProductReward[] = [];
  uploadKeyReward: string; // in case new reward was created we store the temp upload key (to be moved to advertiser folder)

  enabled: boolean;


  async ngOnInit() {
    // Initial load
    this.rewardProductsOptions = this.punchCardCampaignService.rewardProductsOptions;
    this.advertiser = this.punchCardCampaignService.advertiser;

    // Subscribe to reward list updates
    this.punchCardCampaignService.onGetProductFinished
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // Update the reward list
        this.rewardProductsOptions = this.punchCardCampaignService.rewardProductsOptions;
        this.advertiser = this.punchCardCampaignService.advertiser;

        // If we have a selected product reward, check if it still exists in the new list
        const currentProductReward = this.form.get('productReward').value;
        if (currentProductReward) {
          const stillExists = this.rewardProductsOptions.some(
            reward => reward.productSlug === currentProductReward.productSlug
          );
          if (!stillExists) {
            // Clear the selection if the product no longer exists
            this.form.patchValue({ productReward: null });
          }
        }
      });
  }

  async setFormValue(model: RewardData) {
    if (!model || (!model.productReward && !model.coinReward && !model.stampReward))
      return;
    this.enabled = true;

    this.form.patchValue({
      productReward: model.productReward,
      coinReward: model.coinReward,
      stampReward: model.stampReward
    });
    this.form.updateValueAndValidity();
  }

  getFormValue(): RewardData {
    const f: any = this.form.value;
    const hasReward = f.productReward || (f.coinReward && f.coinReward.coins) || (f.stampReward && f.stampReward.stamps);
    let result = (hasReward != 0 && !hasReward)
      ? undefined
      : new RewardData({
        productReward: f.productReward ? new ProductReward({ ...f.productReward }) : undefined,
        coinReward: f.coinReward && f.coinReward.coins ? new CoinReward({ coins: f.coinReward.coins }) : undefined,
        stampReward: f.stampReward && f.stampReward.stamps ? new StampReward({ stamps: f.stampReward.stamps }) : undefined
      });
    return result;
  }

  forceValidatorsToDisplayErrors() {
    // force validators to display errors
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key).markAsTouched();
      this.form.get(key).markAsDirty();
    });
  }

  async onSubmit() {
  }

  onRewardSelected(productReward: ProductReward) {
    this.form.patchValue({
      rewardTitle: (productReward && productReward.rewardTitle) || '',
      rewardDescription: (productReward && productReward.rewardDescription) || ''
    });
  }



  // product reward add
  async onAddRewardProduct() {

    const productReward = await AdvertiserCampaignRewardEditComponent.showDialog(this.dialog, { advertiser: this.advertiser });
    if (!productReward) return;

    this.rewardProductsOptions = [
      productReward,                    // push at 0 index
      ...this.rewardProductsOptions
    ];

    this.uploadKeyReward = productReward.uploadKey;  // this flag must create the product reward the the campaing is created

    this.form.patchValue({
      productReward: productReward,
      rewardTitle: (productReward && productReward.rewardTitle) || '',
      rewardDescription: (productReward && productReward.rewardDescription) || ''
    });
  }
  async onRemoveRewardProduct() {
    this.form.patchValue({ productReward: undefined });
  }


  onEnabledChanged() {
    if (!this.enabled) {
      this.form.patchValue({
        productReward: undefined,
        coinReward: new CoinReward({ coins: undefined }),
        stampReward: new StampReward({ stamps: undefined })
      });
      this.form.updateValueAndValidity();
      return;
    }
  }

  compareFn: ((a: any, b: any) => boolean) | null = (a: any, b: any) => a && b && a.id === b.id;
  compareRewardFn: ((a: CoinReward, b: CoinReward) => boolean) | null = (a: any, b: any) => a && b && a.coins === b.coins;
  compareProductRewardFn: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;
}
